<script setup lang="ts">
import { computed } from 'vue'
import CanvasPreviewSurface, { type PreviewDrawItem, type PreviewHotspotItem } from '@/components/workspace/CanvasPreviewSurface.vue'
import ImageOptionPickerField from '@/components/ImageOptionPickerField.vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import type { SettingsMenuTabProps, SettingsPreviewButtonDraft } from '@/features/workspace/settings/types'

const props = defineProps<SettingsMenuTabProps>()

function handleMenuButtonEnabledChange(item: SettingsPreviewButtonDraft) {
  if (item.enabled && item.index === 0 && props.imageOptionPickerItems.length > 1) {
    item.index = 1
  }
  if (!item.enabled) {
    item.index = 0
  }
}

const previewDrawItems = computed<PreviewDrawItem[]>(() =>
  props.menuPreviewButtons.map((item) => ({
    key: item.key,
    kind: 'image',
    style: item.style,
    imageUrl: item.url,
    placeholderText: item.label,
  })),
)

const previewHotspotItems = computed<PreviewHotspotItem[]>(() =>
  props.menuPreviewButtons.map((item) => ({
    key: item.key,
    style: {
      ...item.style,
      outline: props.activeMenuDragIndex === item.key ? '1px dashed var(--theme-accent)' : 'none',
      cursor: 'move',
    },
    ariaLabel: `${item.label}预览拖拽热区`,
    onPointerdown: (event: PointerEvent) => props.startMenuButtonDrag(item.key, event),
  })),
)
</script>

<template>
  <div v-if="portraitLayout" class="settings-page settings-portrait-page menu-page-portrait">
    <section class="setting-card settings-preview-card settings-portrait-preview menu-preview-card">
      <CanvasPreviewSurface
        host-class="settings-preview-shell menu-preview-shell"
        stage-class="settings-preview-stage menu-preview-stage"
        :background-url="menuBackgroundUrl"
        :design-width="screenWidth"
        :design-height="screenHeight"
        background-empty-text="工程菜单背景图"
        :draw-items="previewDrawItems"
        :hotspot-items="previewHotspotItems"
        :guide-active="previewGuideActive"
        @stage-resize="onPreviewStageResize"
      />
    </section>

    <div class="settings-portrait-props">
      <section class="setting-card menu-resource-card">
        <label class="field-block menu-resource-field">
          <span class="field-label">系统菜单背景图</span>
          <ResourceSelectField
            :model-value="menuSetting.BackPic.name"
            category="graphic"
            :base-folder-path="backgroundGraphicBaseFolderPath"
            display-mode="full"
            store-mode="relative"
            picker-title="选择系统菜单背景图"
            @update:model-value="updateMenuBackPic"
          />
        </label>
      </section>

      <section class="setting-card menu-controls-card">
        <div class="card-title">菜单按钮</div>

        <div class="settings-item-list">
          <div
            v-for="(item, index) in menuSetting.Buttons"
            :key="`menu-button-portrait-${index}`"
            class="settings-item-card menu-button-item"
          >
            <div class="settings-item-row settings-item-row--main">
              <label class="settings-item-enable">
                <input
                  v-model="item.enabled"
                  type="checkbox"
                  @change="handleMenuButtonEnabledChange(item)"
                />
              </label>
              <div class="settings-item-name">{{ item.label }}</div>
              <ImageOptionPickerField
                :model-value="item.index"
                :items="imageOptionPickerItems"
                :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                :disabled="!item.enabled"
                title="选择菜单按钮图片选项"
                @update:model-value="item.index = $event"
              />
            </div>

            <div class="settings-item-row settings-item-row--axis field-axis-grid field-axis-grid--plain menu-button-axis-row">
              <NumberStepperField v-model="item.x" class="cell-input sm" :disabled="!item.enabled" />
              <NumberStepperField v-model="item.y" class="cell-input sm" :disabled="!item.enabled" />
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>

  <div v-else class="settings-page menu-page">
    <div class="menu-grid">
      <div class="menu-left-column">
        <section class="setting-card menu-controls-card">
          <div class="card-title">菜单按钮</div>

          <div class="settings-item-list">
            <div
              v-for="(item, index) in menuSetting.Buttons"
              :key="`menu-button-${index}`"
              class="settings-item-card menu-button-item"
            >
              <div class="settings-item-row settings-item-row--main">
                <label class="settings-item-enable">
                  <input
                    v-model="item.enabled"
                    type="checkbox"
                    @change="handleMenuButtonEnabledChange(item)"
                  />
                </label>
                <div class="settings-item-name">{{ item.label }}</div>
                <ImageOptionPickerField
                  :model-value="item.index"
                  :items="imageOptionPickerItems"
                  :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                  :disabled="!item.enabled"
                  title="选择菜单按钮图片选项"
                  @update:model-value="item.index = $event"
                />
              </div>

              <div class="settings-item-row settings-item-row--axis field-axis-grid field-axis-grid--plain menu-button-axis-row">
                <NumberStepperField v-model="item.x" class="cell-input sm" :disabled="!item.enabled" />
                <NumberStepperField v-model="item.y" class="cell-input sm" :disabled="!item.enabled" />
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="menu-right-column">
        <section class="setting-card menu-resource-card">
          <label class="field-block menu-resource-field">
            <span class="field-label">系统菜单背景图</span>
            <ResourceSelectField
              :model-value="menuSetting.BackPic.name"
              category="graphic"
              :base-folder-path="backgroundGraphicBaseFolderPath"
              display-mode="full"
              store-mode="relative"
              picker-title="选择系统菜单背景图"
              @update:model-value="updateMenuBackPic"
            />
          </label>
        </section>

        <section class="setting-card settings-preview-card menu-preview-card">
          <CanvasPreviewSurface
            host-class="settings-preview-shell menu-preview-shell"
            stage-class="settings-preview-stage menu-preview-stage"
            :background-url="menuBackgroundUrl"
            :design-width="screenWidth"
            :design-height="screenHeight"
            background-empty-text="工程菜单背景图"
            :draw-items="previewDrawItems"
            :hotspot-items="previewHotspotItems"
            :guide-active="previewGuideActive"
            @stage-resize="onPreviewStageResize"
          />
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import './shared.css';

.settings-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding-top: 4px;
  box-sizing: border-box;
}

.menu-grid {
  display: grid;
  grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  gap: 8px;
  height: 100%;
  min-height: 0;
  align-items: stretch;
}

.menu-left-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.menu-right-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.menu-resource-card {
  flex-shrink: 0;
}

.menu-controls-card,
.menu-resource-field :deep(.resource-select-input),
.menu-button-item :deep(.image-option-picker-input) {
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
  border: 1px solid var(--theme-border);
}

.menu-resource-field :deep(.resource-select-trigger),
.menu-button-item :deep(.image-option-picker-trigger) {
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
}

.menu-preview-button {
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: move;
  user-select: none;
  touch-action: none;
  box-sizing: border-box;
}

.menu-preview-button img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  pointer-events: none;
}

.menu-preview-button--dragging {
  border: 1px dashed var(--theme-accent);
}

.menu-button-axis-row {
  padding-left: 0;
}

@media (max-width: 1180px) and (orientation: landscape) {
  .menu-grid {
    grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  }
}

@media (max-width: 1200px) and (orientation: portrait) {
  .menu-grid {
    grid-template-columns: 1fr;
  }
}
</style>
